/**
 * Globalny loader motywu.
 *
 * HTML loadera powinien być osadzony jeden raz w głównym layoucie:
 * templates/_partials/global-loader.tpl
 *
 * Loader jest dostępny globalnie przez:
 * window.ThemeLoader
 */

export interface ThemeLoaderOptions {
  title?: string;
  text?: string;
}

export type ThemeLoaderToken = string;

export interface ThemeLoaderApi {
  /**
   * Pokazuje loader i zwraca token konkretnej operacji.
   */
  show(options?: ThemeLoaderOptions): ThemeLoaderToken | null;

  /**
   * Ukrywa loader przypisany do danego tokenu.
   */
  hide(token: ThemeLoaderToken | null | undefined): void;

  /**
   * Aktualizuje tekst aktywnego loadera.
   *
   * Bez tokenu aktualizuje ostatnio uruchomiony loader.
   */
  update(
    options: ThemeLoaderOptions,
    token?: ThemeLoaderToken | null,
  ): void;

  /**
   * Awaryjnie zamyka wszystkie aktywne loadery.
   */
  hideAll(): void;

  /**
   * Sprawdza, czy loader jest obecnie aktywny.
   */
  isVisible(): boolean;
}

interface ActiveLoader {
  token: ThemeLoaderToken;
  title: string;
  text: string;
}

declare global {
  interface Window {
    ThemeLoader?: ThemeLoaderApi;
  }
}

const LOADER_ID = 'theme-global-loader';

const TITLE_SELECTOR = '[data-theme-loader-title]';
const TEXT_SELECTOR = '[data-theme-loader-text]';

const VISIBLE_CLASS = 'is-visible';

let loaderElement: HTMLElement | null = null;
let titleElement: HTMLElement | null = null;
let textElement: HTMLElement | null = null;

let defaultTitle = '';
let defaultText = '';

let tokenSequence = 0;

let activeLoaders: ActiveLoader[] = [];

/**
 * Pobiera elementy loadera z DOM.
 */
function resolveElements(): boolean {
  if (loaderElement?.isConnected) {
    return true;
  }

  loaderElement = document.getElementById(LOADER_ID);
  titleElement = null;
  textElement = null;

  if (!loaderElement) {
    return false;
  }

  titleElement = loaderElement.querySelector<HTMLElement>(
    TITLE_SELECTOR,
  );

  textElement = loaderElement.querySelector<HTMLElement>(
    TEXT_SELECTOR,
  );

  defaultTitle = titleElement?.textContent?.trim() ?? '';
  defaultText = textElement?.textContent?.trim() ?? '';

  return true;
}

/**
 * Zwraca ostatnio uruchomiony loader.
 *
 * To jego komunikaty powinny być aktualnie wyświetlane.
 */
function getLatestLoader(): ActiveLoader | null {
  if (activeLoaders.length === 0) {
    return null;
  }

  return activeLoaders[activeLoaders.length - 1];
}

/**
 * Ustawia tytuł i tekst loadera.
 */
function renderLoader(loader: ActiveLoader | null): void {
  if (!resolveElements()) {
    return;
  }

  if (titleElement) {
    titleElement.textContent = loader
      ? loader.title
      : defaultTitle;
  }

  if (textElement) {
    textElement.textContent = loader
      ? loader.text
      : defaultText;
  }
}

/**
 * Pokazuje element loadera.
 */
function activateLoader(): void {
  if (!resolveElements() || !loaderElement) {
    return;
  }

  loaderElement.classList.add(VISIBLE_CLASS);
  loaderElement.setAttribute('aria-hidden', 'false');

  if (document.body) {
    document.body.setAttribute('aria-busy', 'true');
  }
}

/**
 * Ukrywa element loadera.
 */
function deactivateLoader(): void {
  if (!resolveElements() || !loaderElement) {
    return;
  }

  loaderElement.classList.remove(VISIBLE_CLASS);
  loaderElement.setAttribute('aria-hidden', 'true');

  if (document.body) {
    document.body.removeAttribute('aria-busy');
  }
}

/**
 * Pokazuje globalny loader.
 */
function show(
  options: ThemeLoaderOptions = {},
): ThemeLoaderToken | null {
  if (!resolveElements()) {
    console.warn(
      `[ThemeLoader] Nie znaleziono elementu #${LOADER_ID}.`,
    );

    return null;
  }

  tokenSequence += 1;

  const loader: ActiveLoader = {
    token: `theme-loader-${tokenSequence}`,
    title:
      typeof options.title === 'string'
        ? options.title
        : defaultTitle,
    text:
      typeof options.text === 'string'
        ? options.text
        : defaultText,
  };

  activeLoaders.push(loader);

  renderLoader(loader);
  activateLoader();

  return loader.token;
}

/**
 * Ukrywa loader należący do wskazanego tokenu.
 */
function hide(
  token: ThemeLoaderToken | null | undefined,
): void {
  if (!token) {
    return;
  }

  const loaderIndex = activeLoaders.findIndex(
    (loader) => loader.token === token,
  );

  if (loaderIndex === -1) {
    return;
  }

  activeLoaders.splice(loaderIndex, 1);

  const latestLoader = getLatestLoader();

  if (!latestLoader) {
    deactivateLoader();
    renderLoader(null);

    return;
  }

  /*
   * Jeżeli nadal działa inna operacja, loader pozostaje widoczny
   * i pokazuje komunikaty ostatniej aktywnej operacji.
   */
  renderLoader(latestLoader);
  activateLoader();
}

/**
 * Aktualizuje komunikat aktywnego loadera.
 */
function update(
  options: ThemeLoaderOptions,
  token?: ThemeLoaderToken | null,
): void {
  if (activeLoaders.length === 0) {
    return;
  }

  let targetLoader: ActiveLoader | null = null;

  if (token) {
    targetLoader =
      activeLoaders.find(
        (loader) => loader.token === token,
      ) ?? null;
  } else {
    targetLoader = getLatestLoader();
  }

  if (!targetLoader) {
    return;
  }

  if (typeof options.title === 'string') {
    targetLoader.title = options.title;
  }

  if (typeof options.text === 'string') {
    targetLoader.text = options.text;
  }

  /*
   * DOM aktualizujemy tylko wtedy, gdy zmieniany loader
   * jest obecnie ostatnim aktywnym loaderem.
   */
  if (targetLoader === getLatestLoader()) {
    renderLoader(targetLoader);
  }
}

/**
 * Zamyka wszystkie aktywne loadery.
 */
function hideAll(): void {
  activeLoaders = [];

  deactivateLoader();
  renderLoader(null);
}

/**
 * Informuje, czy istnieje aktywna operacja.
 */
function isVisible(): boolean {
  return activeLoaders.length > 0;
}

const ThemeLoader: ThemeLoaderApi = {
  show,
  hide,
  update,
  hideAll,
  isVisible,
};

/**
 * Udostępnienie API dla:
 * - pozostałych plików TypeScript,
 * - zwykłych plików JavaScript,
 * - modułów PrestaShop,
 * - kodu inline.
 */
window.ThemeLoader = ThemeLoader;

export default ThemeLoader;